// ─── Internal changelog feature taxonomy ─────────────────────────────────────
// The internal "What's Been Updated" changelog groups every entry under one of
// a fixed, curated set of product features. AI drafting picks a feature from
// this list; the heuristic `classifyFeature` below is both the AI fallback and
// the backfill classifier for existing entries. Keep this list in sync with the
// product's tool surface — new tools should get their own feature bucket.

export const INTERNAL_UPDATE_FEATURES = [
  "Email Express",
  "LP Express",
  "UGC Express",
  "Ads Express",
  "Slideshow Express",
  "Video Express",
  "Persona Intelligence",
  "Brand & Design System",
  "Meme Generator",
  "Meta Ads",
  "Billing & Admin",
  "Internal Tools",
  "Other",
] as const;

export type InternalUpdateFeature = (typeof INTERNAL_UPDATE_FEATURES)[number];

export const DEFAULT_INTERNAL_UPDATE_FEATURE: InternalUpdateFeature = "Other";

export function isValidFeature(v: unknown): v is InternalUpdateFeature {
  return typeof v === "string" && (INTERNAL_UPDATE_FEATURES as readonly string[]).includes(v);
}

// Coerce arbitrary text (possibly from the AI) to a valid feature: exact match
// wins, otherwise fall back to the keyword heuristic. Always returns a member
// of INTERNAL_UPDATE_FEATURES.
export function coerceFeature(v: unknown, contextText = ""): InternalUpdateFeature {
  if (isValidFeature(v)) return v;
  const text = `${typeof v === "string" ? v : ""} ${contextText}`;
  return classifyFeature(text);
}

// Ordered keyword rules — first match wins, so more specific/unique tool names
// come before generic terms. Case-insensitive. Deterministic and cheap; used
// as the AI fallback and to backfill pre-existing entries.
const FEATURE_RULES: { feature: InternalUpdateFeature; patterns: RegExp[] }[] = [
  { feature: "Email Express", patterns: [/\bemail express\b/i, /\bemail\b/i, /\bklaviyo\b/i, /\bmjml\b/i] },
  { feature: "LP Express", patterns: [/\blp express\b/i, /\blanding page/i, /\blanding-page/i, /\bquiz funnel\b/i, /\badvertorial\b/i, /\blisticle\b/i] },
  { feature: "UGC Express", patterns: [/\bugc\b/i, /\bavatar\b/i, /\bvoiceover\b/i, /\bstoryboard\b/i] },
  { feature: "Slideshow Express", patterns: [/\bslideshow\b/i, /\bcarousel\b/i] },
  { feature: "Video Express", patterns: [/\bvideo express\b/i, /\bclaymation\b/i, /\bpixar\b/i, /\bsimpsons\b/i, /\bday.?in.?life\b/i] },
  { feature: "Ads Express", patterns: [/\bads express\b/i, /\bad units?\b/i, /\bad unit\b/i, /\bcreative tinder\b/i, /\bkill crew\b/i, /\binstant ad/i, /\bmy ads\b/i, /\bad librar/i] },
  { feature: "Meme Generator", patterns: [/\bmeme\b/i, /\bimgflip\b/i] },
  { feature: "Persona Intelligence", patterns: [/\bpersona\b/i, /\bnarrative\b/i, /\bcharacter\b/i, /\bideation\b/i] },
  { feature: "Meta Ads", patterns: [/\bmeta ads?\b/i, /\bfacebook\b/i, /\bmeta connect/i, /\bbrand spy\b/i, /\bcompetit/i] },
  { feature: "Brand & Design System", patterns: [/\bbrand\b/i, /\bdesign system\b/i, /\bfont\b/i, /\blogo\b/i, /\bproduct(s)?\b/i, /\boffer(s)?\b/i, /\bdiscount/i, /\bsale/i] },
  { feature: "Billing & Admin", patterns: [/\bbilling\b/i, /\bcredit/i, /\bpricing\b/i, /\bmargin\b/i, /\badmin\b/i, /\bapprov/i, /\bsignup\b/i, /\brbac\b/i, /\bpermission/i, /\bshar(e|ing)\b/i] },
  { feature: "Internal Tools", patterns: [/\bchangelog\b/i, /\bdigest\b/i, /\bonboarding\b/i, /\binternal\b/i, /\bslack\b/i, /\bnotification/i] },
];

export function classifyFeature(text: string): InternalUpdateFeature {
  const s = String(text || "");
  for (const rule of FEATURE_RULES) {
    if (rule.patterns.some((re) => re.test(s))) return rule.feature;
  }
  return DEFAULT_INTERNAL_UPDATE_FEATURE;
}
